
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, buildWsUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, Send, Flag, Trash2, Phone, Sparkles } from "lucide-react";

function formatTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      hour: "numeric",
      minute: "2-digit",
      day: "numeric",
      month: "short",
    });
  } catch {
    return "";
  }
}

export default function CommunityChannel() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [channel, setChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [content, setContent] = useState("");
  const [err, setErr] = useState("");
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const listRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    });
  }, []);

  // Load channel meta + initial messages
  useEffect(() => {
    let live = true;
    setMessages([]);
    setChannel(null);
    Promise.all([
      api.get("/channels"),
      api.get(`/channels/${slug}/messages`),
    ])
      .then(([chRes, msgRes]) => {
        if (!live) return;
        const ch = chRes.data.find((c) => c.slug === slug);
        setChannel(ch || null);
        setMessages(msgRes.data || []);
        setTimeout(scrollToBottom, 50);
      })
      .catch((e) => { if (live) setErr(formatApiError(e)); });
    return () => { live = false; };
  }, [slug, scrollToBottom]);

  // WebSocket
  useEffect(() => {
    const url = buildWsUrl(`/api/ws/community/${slug}`);
    let ws;
    let pingInterval;
    try {
      ws = new WebSocket(url);
    } catch (e) {
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      pingInterval = setInterval(() => {
        try {
          ws.readyState === 1 && ws.send(JSON.stringify({ type: "ping" }));
        } catch { /* noop */ }
      }, 25000);
    };
    ws.onclose = () => {
      setConnected(false);
      if (pingInterval) clearInterval(pingInterval);
    };
    ws.onerror = () => {
      setConnected(false);
    };
    ws.onmessage = (ev) => {
      try {
        const evt = JSON.parse(ev.data);
        if (evt.type === "message") {
          setMessages((prev) => {
            // avoid duplicates by id
            if (prev.some((m) => m.id === evt.data.id)) return prev;
            return [...prev, evt.data];
          });
          setTimeout(scrollToBottom, 30);
        } else if (evt.type === "delete") {
          setMessages((prev) => prev.map((m) => (m.id === evt.id ? { ...m, deleted: true } : m)));
        }
      } catch { /* noop */ }
    };

    return () => {
      if (pingInterval) clearInterval(pingInterval);
      try { ws.close(); } catch { /* noop */ }
    };
  }, [slug, scrollToBottom]);

  const send = async (e) => {
    e.preventDefault();
    const text = content.trim();
    if (!text) return;
    setErr("");
    try {
      const { data } = await api.post(`/channels/${slug}/messages`, { content: text });
      setContent("");
      // Optimistic add (WS will dedupe)
      setMessages((prev) => prev.some((m) => m.id === data.id) ? prev : [...prev, data]);
      setTimeout(scrollToBottom, 30);
    } catch (e2) {
      setErr(formatApiError(e2));
    }
  };

  const onReport = async (id) => {
    const reason = window.prompt("Quick note for the admin (optional):", "") || "";
    try {
      await api.post(`/messages/${id}/report`, { reason });
      window.alert("Thank you. An admin will review this message.");
    } catch (e) {
      window.alert(formatApiError(e));
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await api.delete(`/messages/${id}`);
      setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, deleted: true } : m)));
    } catch (e) {
      window.alert(formatApiError(e));
    }
  };

  return (
    <div data-testid="page-community-channel" className="py-10 lg:py-14">
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 mb-6">
          <Link
            to="/community"
            data-testid="channel-back"
            className="inline-flex items-center gap-2 text-sm text-charcoal-light dark:text-bone/65 hover:text-terracotta transition"
          >
            <ArrowLeft size={16} strokeWidth={1.5} />
            All rooms
          </Link>
          <span
            data-testid="ws-status"
            className={`inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] ${
              connected ? "text-moss" : "text-charcoal-light/70 dark:text-bone/45"
            }`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${connected ? "bg-moss animate-pulse" : "bg-charcoal-light/40"}`} />
            {connected ? "Live" : "Connecting…"}
          </span>
        </div>

        <div className="rounded-[2rem] border border-bone-200 dark:border-charcoal-light/25 bg-bone dark:bg-charcoal-light/10 overflow-hidden flex flex-col h-[72vh]">
          {/* Channel banner */}
          <div className="px-6 lg:px-8 py-5 border-b border-bone-200 dark:border-charcoal-light/25 bg-bone-100/60 dark:bg-charcoal-light/15">
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-terracotta/10 text-terracotta">
                <Sparkles size={18} strokeWidth={1.5} />
              </span>
              <div>
                <h1 data-testid="channel-name" className="font-serif text-2xl text-charcoal dark:text-bone tracking-tight">
                  {channel?.name || "Loading…"}
                </h1>
                {channel?.description && (
                  <p className="text-xs text-charcoal-light dark:text-bone/60 mt-0.5">{channel.description}</p>
                )}
              </div>
            </div>
          </div>

          {/* Message list */}
          <div ref={listRef} data-testid="message-list" className="flex-1 overflow-y-auto px-6 lg:px-8 py-6 space-y-5">
            {messages.length === 0 && (
              <p className="text-sm text-charcoal-light dark:text-bone/55 italic text-center py-12">
                The room is empty. Be the first to say hello.
              </p>
            )}
            {messages.map((m) => (
              <div
                key={m.id}
                data-testid={`message-${m.id}`}
                className="group flex flex-col gap-1"
              >
                <div className="flex items-baseline gap-3">
                  <span className="font-serif text-base text-terracotta tracking-tight">
                    {m.first_name || "Friend"}
                  </span>
                  <span className="text-[0.65rem] uppercase tracking-[0.22em] text-charcoal-light/70 dark:text-bone/45">
                    {formatTime(m.created_at)}
                  </span>
                  {user?.id !== m.user_id && !m.deleted && (
                    <button
                      type="button"
                      onClick={() => onReport(m.id)}
                      data-testid={`report-${m.id}`}
                      className="opacity-0 group-hover:opacity-100 text-[0.65rem] uppercase tracking-[0.22em] text-charcoal-light/70 dark:text-bone/50 hover:text-terracotta transition inline-flex items-center gap-1"
                    >
                      <Flag size={11} strokeWidth={1.5} />
                      Report
                    </button>
                  )}
                  {user?.role === "admin" && !m.deleted && (
                    <button
                      type="button"
                      onClick={() => onDelete(m.id)}
                      data-testid={`delete-${m.id}`}
                      className="opacity-0 group-hover:opacity-100 text-[0.65rem] uppercase tracking-[0.22em] text-charcoal-light/70 dark:text-bone/50 hover:text-terracotta transition inline-flex items-center gap-1"
                    >
                      <Trash2 size={11} strokeWidth={1.5} />
                      Delete
                    </button>
                  )}
                </div>
                {m.deleted ? (
                  <p className="text-sm italic text-charcoal-light/60 dark:text-bone/40">— message removed by an admin —</p>
                ) : (
                  <p className="text-base text-charcoal dark:text-bone leading-relaxed whitespace-pre-wrap break-words">
                    {m.content}
                  </p>
                )}
              </div>
            ))}
          </div>

          {/* Composer */}
          <form onSubmit={send} data-testid="composer" className="px-6 lg:px-8 py-4 border-t border-bone-200 dark:border-charcoal-light/25 bg-bone-100/40 dark:bg-charcoal-light/15">
            {err && <p className="text-xs text-terracotta mb-2">{err}</p>}
            <div className="flex items-end gap-3">
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send(e);
                  }
                }}
                placeholder="Say something kind…"
                rows={2}
                maxLength={2000}
                data-testid="composer-input"
                className="flex-1 resize-none rounded-2xl border border-bone-200 dark:border-charcoal-light/30 bg-bone dark:bg-charcoal-light/10 text-charcoal dark:text-bone px-4 py-3 outline-none focus:border-terracotta transition"
              />
              <button
                type="submit"
                data-testid="composer-send"
                className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-terracotta text-bone hover:bg-terracotta-hover transition shrink-0"
                aria-label="Send"
              >
                <Send size={16} strokeWidth={1.5} />
              </button>
            </div>
          </form>
        </div>

        {/* Crisis */}
        <div data-testid="channel-crisis" className="mt-6 flex items-center gap-3 text-sm text-charcoal-light dark:text-bone/65">
          <Phone size={14} strokeWidth={1.5} className="text-terracotta" />
          In crisis? <a href="tel:000" className="text-terracotta underline">000</a>
          · Lifeline <a href="tel:131114" className="text-terracotta underline">13 11 14</a>
          · <Link to="/resources" className="hover:text-terracotta transition">All resources</Link>
        </div>
      </div>
    </div>
  );
}
